import { Button, DatePicker, Form, Input, Modal, Radio, Select, Alert } from "antd";
import { WarningFilled } from "@ant-design/icons";
import PropTypes from "prop-types";
import React, { useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";

const BLOCK_REASON_OPTIONS = [
    { value: "maintenance", label: "Maintenance" },
    { value: "personal_use", label: "Personal Use" },
    { value: "accident_repair", label: "Accident / Repair" },
    { value: "documentation_issue", label: "Documentation Issue" },
    { value: "other", label: "Other" },
];

const AddBlockModal = ({ open, onClose, carId, onSuccess }) => {
    const { t } = useTranslation("cars");
    const [form] = Form.useForm();
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [blockType, setBlockType] = useState("range");
    const [reason, setReason] = useState("");
    const [conflicts, setConflicts] = useState(null);

    const resetState = () => {
        form.resetFields();
        setBlockType("range");
        setReason("");
        setConflicts(null);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const completeSuccessFlow = (showToast = true) => {
        if (showToast) {
            toast.success({ message: t("carDetail.blockCreatedSuccess") });
        }
        onSuccess();
        handleClose();
    };

    const handleFinish = async (values) => {
        const payload = {
            blockType,
            reason: values.reason,
        };

        if (values.reasonNote) {
            payload.reasonNote = values.reasonNote;
        }

        if (blockType === "range") {
            payload.startDate = values.dateRange[0].format("YYYY-MM-DD");
            payload.endDate = values.dateRange[1].format("YYYY-MM-DD");
        } else {
            payload.specificDates = (values.specificDates || []).map((d) => d.format("YYYY-MM-DD"));
        }

        try {
            setLoading(true);
            const res = await apiClient.post(`/api/car/${carId}/block`, payload);
            if (res?.data?.success) {
                const { hasConflicts, overlappingReservations } = res.data.data;
                if (hasConflicts) {
                    setConflicts(overlappingReservations);
                } else {
                    completeSuccessFlow();
                }
            } else {
                toast.error({
                    message: t("carDetail.blockCreateFailed"),
                    description: res?.data?.message || t("carDetail.unexpectedError"),
                });
            }
        } catch (err) {
            toast.error({
                message: t("carDetail.blockCreateFailed"),
                description: err?.response?.data?.message || t("carDetail.unexpectedError"),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBlockTypeChange = (e) => {
        setBlockType(e.target.value);
        form.resetFields(["dateRange", "specificDates"]);
    };

    return (
        <Modal
            open={open}
            onCancel={() => (conflicts ? completeSuccessFlow(false) : handleClose())}
            maskClosable={false}
            title="Block this car"
            footer={null}
            destroyOnClose>
            {conflicts ? (
                <div>
                    <Alert
                        type="warning"
                        showIcon
                        icon={<WarningFilled />}
                        className="mb-4"
                        message={`This block overlaps with ${conflicts.length} active reservation${conflicts.length !== 1 ? "s" : ""}`}
                        description={
                            <ul style={{ margin: "0.5rem 0 0", padding: 0, listStyle: "none" }}>
                                {conflicts.map((r) => (
                                    <li key={r.id} style={{ marginBottom: "0.25rem" }}>
                                        <strong>#{r.serialID}</strong> — {r.customerName}&nbsp;&nbsp;|&nbsp;&nbsp;
                                        {dayjs(r.pickupDate).format("MMM D")} → {dayjs(r.dropoffDate).format("MMM D")}
                                    </li>
                                ))}
                            </ul>
                        }
                    />
                    <p style={{ color: "#888", fontSize: "0.85rem", marginTop: "0.75rem" }}>
                        The block was created. Review these reservations and contact customers if needed.
                    </p>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                        <Button type="primary" onClick={() => completeSuccessFlow(false)} style={{ backgroundColor: "#5d5dff", borderColor: "#5d5dff" }}>
                            Done
                        </Button>
                    </div>
                </div>
            ) : (
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                    <Form.Item label="Block type">
                        <Radio.Group value={blockType} onChange={handleBlockTypeChange}>
                            <Radio value="range">Date range</Radio>
                            <Radio value="specific_dates">Specific days</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {blockType === "range" ? (
                        <Form.Item
                            label="Date range"
                            name="dateRange"
                            rules={[{ required: true, message: "Please select a date range" }]}>
                            <DatePicker.RangePicker
                                className="w-full"
                                format="YYYY-MM-DD"
                                disabledDate={(d) => d.isBefore(dayjs(), "day")}
                            />
                        </Form.Item>
                    ) : (
                        <Form.Item
                            label="Specific dates"
                            name="specificDates"
                            rules={[{ required: true, message: "Select at least one date", type: "array", min: 1 }]}>
                            <DatePicker
                                multiple
                                className="w-full"
                                format="YYYY-MM-DD"
                                disabledDate={(d) => d.isBefore(dayjs(), "day")}
                                maxTagCount="responsive"
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Reason"
                        name="reason"
                        rules={[{ required: true, message: "Please select a reason" }]}>
                        <Select
                            placeholder="Select reason"
                            onChange={setReason}
                            options={BLOCK_REASON_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Notes"
                        name="reasonNote"
                        rules={
                            reason === "other"
                                ? [{ required: true, message: "Notes are required when reason is 'Other'" }]
                                : []
                        }
                        extra={reason !== "other" ? "Optional" : undefined}>
                        <Input.TextArea
                            rows={2}
                            placeholder={reason === "other" ? "Describe the reason…" : "Optional notes…"}
                        />
                    </Form.Item>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", paddingTop: "0.5rem" }}>
                        <Button onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            htmlType="submit"
                            type="primary"
                            loading={loading}
                            style={{ backgroundColor: "#5d5dff", borderColor: "#5d5dff" }}>
                            Create Block
                        </Button>
                    </div>
                </Form>
            )}
        </Modal>
    );
};

AddBlockModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    carId: PropTypes.string.isRequired,
    onSuccess: PropTypes.func.isRequired,
};

export default AddBlockModal;
